/**
 * TypeScript mirror of the color values in tokens.css / tokens-dark.css.
 *
 * Most components style themselves with CSS custom properties (via Tailwind classes
 * generated from tailwind-preset.cjs) and never need raw hex in JS. Charts are the
 * exception: an SVG/canvas mark needs a literal paint value at render time, and the
 * "color follows the entity, not its rank" rule (§10.9) requires a stable, ordered
 * array a chart primitive can index into deterministically.
 *
 * `tokens.consistency.test.ts` parses tokens.css/tokens-dark.css and asserts these
 * arrays match byte-for-byte — so this file cannot silently drift from the CSS.
 */
/** DESIGN_SYSTEM.md §10.2 — light theme, fixed order, never cycled. */
export const CATEGORICAL_LIGHT = [
    '#3065b2',
    '#b077cc',
    '#9a4176',
    '#d6724f',
    '#855e09',
    '#869e32',
    '#067859',
    '#16a2c1',
];
/** DESIGN_SYSTEM.md §10.2 — dark theme, same 8 entities, re-stepped. */
export const CATEGORICAL_DARK = [
    '#4e68ce',
    '#af77e1',
    '#b3417d',
    '#e66a51',
    '#9e6201',
    '#9b9c0b',
    '#048663',
    '#00a6c6',
];
/** DESIGN_SYSTEM.md §10.2 — "Other" is a reserved neutral, never part of the 8-hue set. */
export const CATEGORICAL_OTHER = { light: '#7c8592', dark: '#a6adb8' };
/** DESIGN_SYSTEM.md §10.3 — sequential magnitude ramp (reuses the primary ramp). Light only. */
export const SEQUENTIAL_LIGHT = [
    '#eaf1fb',
    '#cfe0f5',
    '#a8c7ea',
    '#7dacdd',
    '#4f8ece',
    '#2e6fb8',
    '#1f4e8c',
    '#173d6e',
    '#11304f',
    '#0b1f33',
];
/** DESIGN_SYSTEM.md §10.4 — diverging pair, light only (dark not yet re-stepped, §8.6). */
export const DIVERGING_LIGHT = {
    good3: '#0b7a6e',
    good2: '#4fa89b',
    good1: '#a8d4cc',
    mid: '#c9ced6',
    bad1: '#e3b27a',
    bad2: '#c4823a',
    bad3: '#a64510',
};
export const STATUS_TOKENS = ['info', 'good', 'warning', 'serious', 'critical', 'neutral'];
/**
 * Assigns a categorical color to an ordered list of entity keys, stably: the same key
 * always gets the same slot regardless of which other keys are present or removed
 * (§10.9 — "color follows the entity, not its rank"). Falls back to `categorical-other`
 * once the 8 slots are exhausted (§10.2's 7+-series structural rule is enforced by the
 * caller — see the chart primitives — not by this function).
 */
export function assignCategoricalColor(key, allKeysInStableOrder, theme = 'light') {
    const palette = theme === 'light' ? CATEGORICAL_LIGHT : CATEGORICAL_DARK;
    const index = allKeysInStableOrder.indexOf(key);
    if (index < 0 || index >= palette.length) {
        return theme === 'light' ? CATEGORICAL_OTHER.light : CATEGORICAL_OTHER.dark;
    }
    return palette[index];
}
//# sourceMappingURL=tokens.js.map